import React from 'react';

import { ICONS, Icon } from '../invoices/kit';
import {
  AUDIENCE,
  CLOSED,
  OPEN,
  SORT,
  SORT_LABELS,
} from './lib/boardFilters';
import { IMPACT_ORDER, STATE_ORDER, impactMeta, stateMeta } from './lib/feedbackTypes';
import { TYPE_CHOICES } from './lib/feedbackDraft';
import { SCREEN_OPTIONS } from './lib/screens';

/**
 * The controls above the board.
 *
 * Open and Closed lead the tabs because the first question in any triage
 * session is "what is still ours". The individual states follow for when
 * someone wants only the shipped list or only what is in progress.
 */

const TABS = [
  { id: OPEN, label: 'Open' },
  ...STATE_ORDER.map((state) => ({ id: state, label: stateMeta(state).label })),
  { id: CLOSED, label: 'Closed' },
];

export function StateTabs({ value, counts, onChange }) {
  return (
    <div className="fb-tabs" role="tablist" aria-label="Filter by status">
      {TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          className="fb-tab"
          aria-selected={value === tab.id}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
          <span className="fb-tab__n">{counts[tab.id] || 0}</span>
        </button>
      ))}
    </div>
  );
}

export function FilterBar({ filters, onChange, onExport, exportDisabled }) {
  const set = (field) => (event) => onChange(field, event.target.value);

  return (
    <div className="fb-bar">
      <div className="fb-bar__grow iv-search">
        <span className="fb-bar__icon iv-search__icon">
          <Icon path={ICONS.search} size={15} />
        </span>
        <input
          className="iv-input"
          type="search"
          value={filters.q}
          placeholder="Search titles, detail, replies and people"
          aria-label="Search the board"
          onChange={set('q')}
        />
      </div>

      <select className="iv-select" value={filters.type} onChange={set('type')} aria-label="Type">
        <option value="">Any type</option>
        {TYPE_CHOICES.map((choice) => (
          <option key={choice.id} value={choice.id}>
            {choice.label}
          </option>
        ))}
      </select>

      <select
        className="iv-select"
        value={filters.impact}
        onChange={set('impact')}
        aria-label="Impact"
      >
        <option value="">Any impact</option>
        {IMPACT_ORDER.map((impact) => (
          <option key={impact} value={impact}>
            {impactMeta(impact).label}
          </option>
        ))}
      </select>

      <select
        className="iv-select"
        value={filters.screen}
        onChange={set('screen')}
        aria-label="Screen"
      >
        <option value="">Any screen</option>
        {SCREEN_OPTIONS.map((screen) => (
          <option key={screen.id} value={screen.id}>
            {screen.label}
          </option>
        ))}
      </select>

      <select
        className="iv-select"
        value={filters.audience}
        onChange={set('audience')}
        aria-label="Raised by"
      >
        <option value={AUDIENCE.ALL}>Anyone</option>
        <option value={AUDIENCE.BRAND}>Brand users</option>
        <option value={AUDIENCE.INTERNAL}>Internal team</option>
      </select>

      <select className="iv-select" value={filters.sort} onChange={set('sort')} aria-label="Sort">
        {Object.values(SORT).map((sort) => (
          <option key={sort} value={sort}>
            {SORT_LABELS[sort]}
          </option>
        ))}
      </select>

      <button
        type="button"
        className="iv-btn iv-btn--ghost iv-btn--sm"
        onClick={onExport}
        disabled={exportDisabled}
      >
        <Icon path={ICONS.download} size={14} />
        Export
      </button>
    </div>
  );
}

/**
 * Which screens hurt.
 *
 * One complaint about Discover is noise; fourteen is a decision. Clicking
 * a row filters the board to it, so the number is a way in rather than a
 * statistic to look at.
 */
export function ScreenRollup({ rows, active, onPick }) {
  if (rows.length === 0) return null;
  const peak = rows.reduce((max, row) => Math.max(max, row.total), 0) || 1;

  return (
    <div className="fb-panel">
      <p className="fb-panel__title">Where it hurts</p>
      <div className="fb-rollup">
        {rows.map((row) => (
          <button
            key={row.id}
            type="button"
            className="fb-rollup__row"
            aria-pressed={active === row.id}
            onClick={() => onPick(active === row.id ? '' : row.id)}
          >
            <span className="fb-rollup__name">{row.label}</span>
            <span className="fb-rollup__bar">
              <span className="fb-rollup__fill" style={{ width: `${(row.total / peak) * 100}%` }} />
            </span>
            <span className="fb-rollup__n">{row.open}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
